import React from "react";
import Accordion from "react-bootstrap/Accordion";
import Card from "react-bootstrap/Card";
import Icon from "../../common/Icon";
import BaseProps from "../../types/BaseProps";
import classNames from "classnames";
import {
  ValidationDataModel,
  ValidationResultModel,
  ValidationType
} from "../../services/models/validation-model";
import {ValidationDatatableRenderer} from "./ValidationDatatableRenderer";

import './ValidationMessages.scss';

type ValidationMessagesProps = BaseProps & {
  model: ValidationResultModel[]
}

type ValidationMessagesState = {
  collapsed: boolean;
}

/**
 * A collapsible area that can render json and ls+json formatted error or warning messages with
 * different renderers.
 */
class ValidationMessages extends React.Component<ValidationMessagesProps, ValidationMessagesState> {

  constructor(props: ValidationMessagesProps) {
    super(props);
    this.state = {
      collapsed: false
    };
  }

  onCollapse = (): void => {
    this.setState({
      collapsed: !this.state.collapsed
    });
  }

  getSectionByType(type: ValidationType, validationData: ValidationResultModel) {
    const sectionByTypeMapping = {
      [ValidationType.SCHEMA_VALIDATION]: <SchemaValidationMessages
        validationData={validationData}
        collapsed={this.state.collapsed}
        onCollapse={this.onCollapse}/>,
      [ValidationType.DATA_VALIDATION]: <DataValidationMessages
        validationData={validationData}
        collapsed={this.state.collapsed}
        onCollapse={this.onCollapse}/>,
      [ValidationType.UNSPECIFIED_ERROR]: <SchemaValidationMessages
        validationData={validationData}
        collapsed={this.state.collapsed}
        onCollapse={this.onCollapse}/>
    };
    return sectionByTypeMapping[type];
  }

  render() {
    const {model} = this.props;
    if (model.length) {
      return (
        <Accordion defaultActiveKey="0" className="ValidationMessages">
          {
            model.map((result) => this.getSectionByType(result.type || ValidationType.UNSPECIFIED_ERROR, result))
          }
        </Accordion>
      );
    }
    return false;
  }
}

function SchemaValidationMessages(props: {
  onCollapse: () => void,
  collapsed: boolean,
  validationData: ValidationResultModel
}) {
  const sectionTitle = props.validationData.errors ? 'Schema Validation Errors' : 'Schema Validation Warnings';
  const data = props.validationData.errors || props.validationData.warnings || [];
  const cardClasses = classNames('SchemaValidationMessages', {
    'border-danger': props.validationData.errors,
    'border-warning': props.validationData.warnings
  });

  return <Card className={cardClasses} key={props.validationData.type}>
    <Accordion.Toggle as={Card.Header}
                      eventKey="0"
                      className={"MessagesHeader SchemaValidationMessagesHeader"}
                      onClick={props.onCollapse}>
      <span>{sectionTitle}</span>
      <CaretIcon collapsed={props.collapsed}/>
    </Accordion.Toggle>

    <Accordion.Collapse eventKey="0">
      <Card.Body>
        <ValidationDatatableRenderer data={data}/>
      </Card.Body>
    </Accordion.Collapse>
  </Card>
}

function DataValidationMessages(props: {
  onCollapse: () => void,
  collapsed: boolean,
  validationData: ValidationResultModel
}) {
  const data: ValidationDataModel[] = [];
  if (props.validationData.errors) {
    data.push(...props.validationData.errors);
  }
  if (props.validationData.warnings) {
    data.push(...props.validationData.warnings);
  }
  if (props.validationData.info) {
    data.push(...props.validationData.info);
  }
  const cardClasses = classNames('DataValidationMessages', {
    'border-danger': props.validationData.errors?.length,
    'border-warning': props.validationData.warnings?.length && !props.validationData.errors?.length,
    'border-info': !props.validationData.warnings?.length && !props.validationData.errors?.length,
  });

  return <Card className={cardClasses} key={props.validationData.type}>
    <Accordion.Toggle as={Card.Header}
                      eventKey="1"
                      className={"MessagesHeader DataValidationMessagesHeader"}
                      onClick={props.onCollapse}>
      <span>Data Validation Errors</span>
      <CaretIcon collapsed={props.collapsed}/>
    </Accordion.Toggle>

    <Accordion.Collapse eventKey="1">
      <Card.Body>
        <ValidationDatatableRenderer data={data}/>
      </Card.Body>
    </Accordion.Collapse>
  </Card>
}

function CaretIcon(props: ValidationMessagesState) {
  return <Icon icon="angle-down" size="2x" transform={{rotate: props.collapsed ? 180 : 0}}/>;
}

export default ValidationMessages;
